"use client";

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { dmsApi } from "@/lib/api";
import { useAuth } from "@/hooks/use-auth";
import { toast } from "@/hooks/use-toast";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Loader2,
  Plus,
  RefreshCw,
  Trash2,
  ArrowDownLeft,
  ArrowUpRight,
  QrCode,
  Download,
  Printer,
  Paperclip,
  History,
  Mails,
  Search,
  ScanText,
  Pencil,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { QrPdfPlacer, type QrPlacementResult } from "@/components/correspondence/qr-pdf-placer";

const TYPE_LABELS: Record<string, string> = {
  incoming: "Incoming",
  outgoing: "Outgoing",
};

const STATUS_LABELS: Record<string, string> = {
  registered: "Registered",
  under_review: "Under Review",
  completed: "Completed",
  archived: "Archived",
};

const STATUS_ORDER = ["registered", "under_review", "completed", "archived"];

const ACTION_LABELS: Record<string, string> = {
  transaction_created: "Transaction registered",
  transaction_updated: "Details updated",
  status_changed: "Status changed",
  attachments_added: "Attachments added",
  document_attached: "Main document attached",
  document_replaced: "Main document replaced",
  document_scanned: "Document indexed for search",
  qr_applied_to_pdf: "QR inserted into PDF",
  transaction_deleted: "Transaction deleted",
};

function statusVariant(status: string): "default" | "secondary" | "destructive" | "outline" {
  if (status === "completed") return "default";
  if (status === "under_review") return "secondary";
  if (status === "archived") return "outline";
  return "secondary";
}

function formatDate(value?: string | null) {
  if (!value) return "—";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toISOString().slice(0, 10);
}

function formatDateTime(value?: string | null) {
  if (!value) return "—";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleString();
}

function todayInputValue() {
  return new Date().toISOString().slice(0, 10);
}

const EMPTY_FORM = {
  type: "incoming",
  title: "",
  senderName: "",
  senderOrganization: "",
  recipientName: "",
  recipientOrganization: "",
  transactionDate: "",
  status: "registered",
  notes: "",
};

export function CorrespondencePage({
  departmentId,
  departmentName,
  initialTransactionId,
  initialOpenFileId,
  onInitialTransactionHandled,
}: {
  departmentId?: string;
  departmentName?: string;
  initialTransactionId?: string;
  /** When set (e.g. from QR scan), open this file view immediately after loading the transaction. */
  initialOpenFileId?: string;
  onInitialTransactionHandled?: () => void;
}) {
  const { hasPermission, isSuperAdmin, user } = useAuth();
  const canManage = isSuperAdmin || hasPermission("manage_correspondence");
  const canDelete = isSuperAdmin || hasPermission("delete_correspondence");

  const userDepartmentId = useMemo(() => {
    const raw = (user as { departmentId?: string | { _id?: string } } | null)?.departmentId;
    if (typeof raw === "object" && raw?._id) return String(raw._id);
    if (typeof raw === "string") return raw;
    return "";
  }, [user]);

  const deptParams = useMemo(() => {
    const id = isSuperAdmin ? departmentId : userDepartmentId;
    return id ? { departmentId: id } : undefined;
  }, [isSuperAdmin, departmentId, userDepartmentId]);

  const activeDepartmentId = deptParams?.departmentId;

  const [loading, setLoading] = useState(true);
  const [dashboard, setDashboard] = useState<any>(null);
  const [items, setItems] = useState<any[]>([]);
  const [total, setTotal] = useState(0);

  const [search, setSearch] = useState("");
  const [typeFilter, setTypeFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");

  const [createOpen, setCreateOpen] = useState(false);
  const [form, setForm] = useState({ ...EMPTY_FORM });
  const [documentFile, setDocumentFile] = useState<File | null>(null);
  const [attachmentFiles, setAttachmentFiles] = useState<File[]>([]);
  const [saving, setSaving] = useState(false);
  const documentInputRef = useRef<HTMLInputElement | null>(null);
  const attachmentsInputRef = useRef<HTMLInputElement | null>(null);

  const [detail, setDetail] = useState<any | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailStatus, setDetailStatus] = useState("registered");
  const [statusSaving, setStatusSaving] = useState(false);
  const [history, setHistory] = useState<any[]>([]);
  const [qr, setQr] = useState<{ dataUrl: string | null; targetUrl: string } | null>(null);
  const [qrLoading, setQrLoading] = useState(false);
  const [qrDialogOpen, setQrDialogOpen] = useState(false);
  const [qrEditorOpen, setQrEditorOpen] = useState(false);
  const [pdfInfo, setPdfInfo] = useState<{
    hasDocument: boolean;
    isPdf: boolean;
    pageCount: number;
    pages?: Array<{ pageNumber: number; width: number; height: number }>;
    originalName?: string;
    dmsFileId?: string;
  } | null>(null);
  const [applyingQrToPdf, setApplyingQrToPdf] = useState(false);

  const [deleteTarget, setDeleteTarget] = useState<any | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [scanRetrying, setScanRetrying] = useState(false);

  const [editForm, setEditForm] = useState({
    title: "",
    senderName: "",
    senderOrganization: "",
    recipientName: "",
    recipientOrganization: "",
    transactionDate: "",
    notes: "",
  });
  const [editSaving, setEditSaving] = useState(false);
  const [detailDocumentFile, setDetailDocumentFile] = useState<File | null>(null);
  const [detailAttachmentFiles, setDetailAttachmentFiles] = useState<File[]>([]);
  const [fileUploading, setFileUploading] = useState(false);
  const detailDocumentInputRef = useRef<HTMLInputElement | null>(null);
  const detailAttachmentsInputRef = useRef<HTMLInputElement | null>(null);

  const listParams = useMemo(() => {
    return {
      ...(deptParams || {}),
      ...(search ? { search } : {}),
      ...(typeFilter !== "all" ? { type: typeFilter } : {}),
      ...(statusFilter !== "all" ? { status: statusFilter } : {}),
      ...(dateFrom ? { dateFrom } : {}),
      ...(dateTo ? { dateTo } : {}),
      limit: "100",
    };
  }, [deptParams, search, typeFilter, statusFilter, dateFrom, dateTo]);

  const load = useCallback(async () => {
    if (!deptParams?.departmentId) {
      setDashboard(null);
      setItems([]);
      setTotal(0);
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const [dash, list] = await Promise.all([
        dmsApi.correspondence.dashboard({
          ...(deptParams || {}),
          ...(dateFrom ? { dateFrom } : {}),
          ...(dateTo ? { dateTo } : {}),
        }),
        dmsApi.correspondence.list(listParams),
      ]);
      setDashboard(dash || null);
      setItems(Array.isArray(list?.items) ? list.items : []);
      setTotal(typeof list?.total === "number" ? list.total : 0);
    } catch (err: any) {
      toast({
        variant: "destructive",
        title: "Could not load transactions",
        description: err?.error || err?.message || "Please try again.",
      });
      setDashboard(null);
      setItems([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  }, [deptParams, listParams, dateFrom, dateTo]);

  useEffect(() => {
    load();
  }, [load]);

  const resetForm = useCallback(() => {
    setForm({ ...EMPTY_FORM, transactionDate: todayInputValue() });
    setDocumentFile(null);
    setAttachmentFiles([]);
    if (documentInputRef.current) documentInputRef.current.value = "";
    if (attachmentsInputRef.current) attachmentsInputRef.current.value = "";
  }, []);

  const openCreate = useCallback(
    (type: "incoming" | "outgoing") => {
      resetForm();
      setForm({ ...EMPTY_FORM, type, transactionDate: todayInputValue() });
      setCreateOpen(true);
    },
    [resetForm]
  );

  const handleCreate = async () => {
    if (!activeDepartmentId) {
      toast({
        variant: "destructive",
        title: "Cannot save",
        description: isSuperAdmin
          ? "Select a department in the sidebar first."
          : "Your user account has no department assigned.",
      });
      return;
    }
    if (!form.title.trim()) {
      toast({ variant: "destructive", title: "Title required", description: "Enter the document title or subject." });
      return;
    }

    setSaving(true);
    try {
      const created = await dmsApi.correspondence.create(
        {
          type: form.type,
          title: form.title.trim(),
          senderName: form.senderName.trim(),
          senderOrganization: form.senderOrganization.trim(),
          recipientName: form.recipientName.trim(),
          recipientOrganization: form.recipientOrganization.trim(),
          transactionDate: form.transactionDate,
          status: form.status,
          notes: form.notes,
        },
        { document: documentFile, attachments: attachmentFiles },
        { departmentId: activeDepartmentId }
      );

      toast({
        title: "Transaction registered",
        description: `Transaction number ${created?.transactionNumber || ""} generated.`,
      });
      setCreateOpen(false);
      resetForm();
      await load();
      if (created?._id) void openDetail(created);
    } catch (err: any) {
      toast({
        variant: "destructive",
        title: "Could not save transaction",
        description: err?.error || err?.message || "Please try again.",
      });
    } finally {
      setSaving(false);
    }
  };

  const openDetail = useCallback(
    async (item: any) => {
      setDetail(item);
      setDetailStatus(item?.status || "registered");
      setHistory([]);
      setQr(null);
      setDetailDocumentFile(null);
      setDetailAttachmentFiles([]);
      if (detailDocumentInputRef.current) detailDocumentInputRef.current.value = "";
      if (detailAttachmentsInputRef.current) detailAttachmentsInputRef.current.value = "";
      setEditForm({
        title: item?.title || "",
        senderName: item?.senderName || "",
        senderOrganization: item?.senderOrganization || "",
        recipientName: item?.recipientName || "",
        recipientOrganization: item?.recipientOrganization || "",
        transactionDate: item?.transactionDate
          ? String(item.transactionDate).slice(0, 10)
          : "",
        notes: item?.notes || "",
      });
      setDetailLoading(true);
      try {
        const [full, hist] = await Promise.all([
          dmsApi.correspondence.get(String(item._id), deptParams),
          dmsApi.correspondence.history(String(item._id), deptParams).catch(() => ({ items: [] })),
        ]);
        setDetail(full);
        setDetailStatus(full?.status || "registered");
        setEditForm({
          title: full?.title || "",
          senderName: full?.senderName || "",
          senderOrganization: full?.senderOrganization || "",
          recipientName: full?.recipientName || "",
          recipientOrganization: full?.recipientOrganization || "",
          transactionDate: full?.transactionDate
            ? String(full.transactionDate).slice(0, 10)
            : "",
          notes: full?.notes || "",
        });
        setHistory(Array.isArray(hist?.items) ? hist.items : []);
      } catch (err: any) {
        toast({
          variant: "destructive",
          title: "Could not open transaction",
          description: err?.error || err?.message || "Please try again.",
        });
      } finally {
        setDetailLoading(false);
      }
    },
    [deptParams]
  );

  const openQrDialog = useCallback(
    async (id: string) => {
      setQrDialogOpen(true);
      setQr(null);
      setPdfInfo(null);
      setQrLoading(true);
      try {
        const [qrData, pdfData] = await Promise.all([
          dmsApi.correspondence.qr(id, deptParams),
          dmsApi.correspondence.documentPdfInfo(id, deptParams).catch(() => ({
            hasDocument: false,
            isPdf: false,
            pageCount: 0,
          })),
        ]);
        setQr({ dataUrl: qrData?.dataUrl || null, targetUrl: qrData?.targetUrl || "" });
        setPdfInfo(pdfData);
        if (!qrData?.dataUrl) {
          toast({
            variant: "destructive",
            title: "QR image unavailable",
            description: "The transaction link is shown instead.",
          });
        }
      } catch (err: any) {
        setQrDialogOpen(false);
        toast({
          variant: "destructive",
          title: "Could not load QR code",
          description: err?.error || err?.message || "Please try again.",
        });
      } finally {
        setQrLoading(false);
      }
    },
    [deptParams]
  );

  const fetchPdfForEditor = useCallback(async () => {
    if (!detail?.dmsFileId) throw new Error("No document file");
    const token = typeof window !== "undefined" ? localStorage.getItem("dms_token") : null;
    const url = dmsApi.correspondence.getFileViewUrl(String(detail.dmsFileId));
    const res = await fetch(url, {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    });
    if (!res.ok) throw new Error("Could not load PDF");
    return res.arrayBuffer();
  }, [detail?.dmsFileId]);

  const handleApplyCustomQr = async (placement: QrPlacementResult) => {
    if (!detail?._id) return;
    setApplyingQrToPdf(true);
    try {
      await dmsApi.correspondence.applyQrToPdf(String(detail._id), placement, deptParams);
      toast({
        title: "QR inserted into PDF",
        description: placement.addNewPage
          ? "QR code placed on the new page where you positioned it."
          : `QR code placed on page ${placement.pageNumber}.`,
      });
      setQrEditorOpen(false);
      const hist = await dmsApi.correspondence.history(String(detail._id), deptParams).catch(() => ({ items: [] }));
      setHistory(Array.isArray(hist?.items) ? hist.items : []);
      const refreshedPdf = await dmsApi.correspondence.documentPdfInfo(String(detail._id), deptParams);
      setPdfInfo(refreshedPdf);
    } catch (err: any) {
      toast({
        variant: "destructive",
        title: "Could not insert QR into PDF",
        description: err?.error || err?.message || "Please try again.",
      });
    } finally {
      setApplyingQrToPdf(false);
    }
  };

  /** Poll while document content is being indexed for search. */
  useEffect(() => {
    if (!detail?._id) return;
    const status = detail.scanStatus;
    if (status !== "pending" && status !== "processing") return;

    const timer = window.setInterval(async () => {
      try {
        const full = await dmsApi.correspondence.get(String(detail._id), deptParams);
        setDetail((prev: any) => (prev && String(prev._id) === String(full._id) ? { ...prev, ...full } : prev));
      } catch {
        /* ignore */
      }
    }, 2500);

    return () => window.clearInterval(timer);
  }, [detail?._id, detail?.scanStatus, deptParams]);

  const handleRetryScan = async () => {
    if (!detail?._id) return;
    setScanRetrying(true);
    try {
      const updated = await dmsApi.correspondence.retryScan(String(detail._id), deptParams);
      setDetail(updated);
      toast({
        title: "Document scanned",
        description:
          updated?.scanStatus === "ready"
            ? "Content is now searchable."
            : updated?.scanError || `Status: ${updated?.scanStatus || "done"}`,
      });
      const hist = await dmsApi.correspondence.history(String(detail._id), deptParams).catch(() => ({ items: [] }));
      setHistory(Array.isArray(hist?.items) ? hist.items : []);
    } catch (err: any) {
      toast({
        variant: "destructive",
        title: "Scan failed",
        description: err?.error || err?.message || "Please try again.",
      });
    } finally {
      setScanRetrying(false);
    }
  };

  /** Auto-open the transaction that a scanned QR code pointed at, and open its file. */
  useEffect(() => {
    if (!initialTransactionId || !deptParams?.departmentId) return;
    const fileIdToOpen = initialOpenFileId;
    void (async () => {
      await openDetail({ _id: initialTransactionId });
      if (fileIdToOpen) {
        const url = dmsApi.correspondence.getFileViewUrl(fileIdToOpen);
        window.open(url, "_blank", "noopener,noreferrer");
      }
      onInitialTransactionHandled?.();
    })();
  }, [initialTransactionId, initialOpenFileId, deptParams, openDetail, onInitialTransactionHandled]);

  const handleStatusSave = async () => {
    if (!detail?._id || detailStatus === detail.status) return;
    setStatusSaving(true);
    try {
      const updated = await dmsApi.correspondence.update(String(detail._id), { status: detailStatus }, deptParams);
      setDetail(updated);
      toast({ title: "Status updated", description: `Now ${STATUS_LABELS[detailStatus] || detailStatus}.` });
      const hist = await dmsApi.correspondence.history(String(detail._id), deptParams).catch(() => ({ items: [] }));
      setHistory(Array.isArray(hist?.items) ? hist.items : []);
      await load();
    } catch (err: any) {
      toast({
        variant: "destructive",
        title: "Could not update status",
        description: err?.error || err?.message || "Please try again.",
      });
    } finally {
      setStatusSaving(false);
    }
  };

  const handleEditSave = async () => {
    if (!detail?._id) return;
    if (!editForm.title.trim()) {
      toast({ variant: "destructive", title: "Title required", description: "Enter the document title or subject." });
      return;
    }
    setEditSaving(true);
    try {
      const updated = await dmsApi.correspondence.update(
        String(detail._id),
        {
          title: editForm.title.trim(),
          senderName: editForm.senderName.trim(),
          senderOrganization: editForm.senderOrganization.trim(),
          recipientName: editForm.recipientName.trim(),
          recipientOrganization: editForm.recipientOrganization.trim(),
          transactionDate: editForm.transactionDate || undefined,
          notes: editForm.notes,
        },
        deptParams
      );
      setDetail(updated);
      toast({ title: "Transaction updated", description: "Details saved." });
      const hist = await dmsApi.correspondence.history(String(detail._id), deptParams).catch(() => ({ items: [] }));
      setHistory(Array.isArray(hist?.items) ? hist.items : []);
      await load();
    } catch (err: any) {
      toast({
        variant: "destructive",
        title: "Could not save changes",
        description: err?.error || err?.message || "Please try again.",
      });
    } finally {
      setEditSaving(false);
    }
  };

  const handleUploadMainDocument = async () => {
    if (!detail?._id || !detailDocumentFile) return;
    setFileUploading(true);
    try {
      const updated = await dmsApi.correspondence.setDocument(
        String(detail._id),
        detailDocumentFile,
        deptParams
      );
      setDetail(updated);
      setDetailDocumentFile(null);
      if (detailDocumentInputRef.current) detailDocumentInputRef.current.value = "";
      toast({
        title: detail.dmsFileId ? "Document replaced" : "Document attached",
        description: "Main file saved on this transaction.",
      });
      const hist = await dmsApi.correspondence.history(String(detail._id), deptParams).catch(() => ({ items: [] }));
      setHistory(Array.isArray(hist?.items) ? hist.items : []);
      await load();
    } catch (err: any) {
      toast({
        variant: "destructive",
        title: "Could not upload document",
        description: err?.error || err?.message || "Please try again.",
      });
    } finally {
      setFileUploading(false);
    }
  };

  const handleUploadAttachments = async () => {
    if (!detail?._id || detailAttachmentFiles.length === 0) return;
    setFileUploading(true);
    try {
      const updated = await dmsApi.correspondence.addAttachments(
        String(detail._id),
        detailAttachmentFiles,
        deptParams
      );
      setDetail(updated);
      setDetailAttachmentFiles([]);
      if (detailAttachmentsInputRef.current) detailAttachmentsInputRef.current.value = "";
      toast({
        title: "Attachments added",
        description: `${detailAttachmentFiles.length} file(s) attached.`,
      });
      const hist = await dmsApi.correspondence.history(String(detail._id), deptParams).catch(() => ({ items: [] }));
      setHistory(Array.isArray(hist?.items) ? hist.items : []);
      await load();
    } catch (err: any) {
      toast({
        variant: "destructive",
        title: "Could not add attachments",
        description: err?.error || err?.message || "Please try again.",
      });
    } finally {
      setFileUploading(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget?._id) return;
    setDeleting(true);
    try {
      await dmsApi.correspondence.delete(String(deleteTarget._id), deptParams);
      toast({ title: "Transaction deleted", description: deleteTarget.transactionNumber || "" });
      setDeleteTarget(null);
      if (detail && String(detail._id) === String(deleteTarget._id)) setDetail(null);
      await load();
    } catch (err: any) {
      toast({
        variant: "destructive",
        title: "Could not delete transaction",
        description: err?.error || err?.message || "Please try again.",
      });
    } finally {
      setDeleting(false);
    }
  };

  const printQr = () => {
    if (!qr?.dataUrl || !detail) return;
    const win = window.open("", "_blank", "width=420,height=560");
    if (!win) return;
    win.document.write(`
      <html>
        <head><title>${detail.transactionNumber}</title></head>
        <body style="font-family:sans-serif;text-align:center;padding:24px">
          <img src="${qr.dataUrl}" style="width:260px;height:260px" />
          <h2 style="margin:12px 0 4px;font-size:18px">${detail.transactionNumber}</h2>
          <p style="margin:0;font-size:13px;color:#555">${(detail.title || "").replace(/</g, "&lt;")}</p>
          <script>window.onload = function(){ window.print(); }</script>
        </body>
      </html>
    `);
    win.document.close();
  };

  if (isSuperAdmin && !departmentId) {
    return (
      <div className="flex h-full flex-col items-center justify-center gap-3 p-8 text-center">
        <Mails className="h-12 w-12 text-muted-foreground/50" />
        <h2 className="text-lg font-semibold">Select a department</h2>
        <p className="max-w-md text-sm text-muted-foreground">
          Choose a department from the sidebar to register and track its incoming and outgoing documents.
        </p>
      </div>
    );
  }

  if (!isSuperAdmin && !userDepartmentId) {
    return (
      <div className="flex h-full flex-col items-center justify-center gap-3 p-8 text-center">
        <Mails className="h-12 w-12 text-muted-foreground/50" />
        <h2 className="text-lg font-semibold">No department assigned</h2>
        <p className="max-w-md text-sm text-muted-foreground">
          Your user account must be assigned to a department before you can use this module.
        </p>
      </div>
    );
  }

  const statusCounts = dashboard?.statusCounts || {};

  return (
    <div className="flex h-full flex-col overflow-hidden">
      <div className="flex flex-wrap items-center justify-between gap-3 border-b p-4">
        <div>
          <h1 className="text-xl font-semibold">Incoming &amp; Outgoing Documents</h1>
          <p className="text-sm text-muted-foreground">
            {departmentName ? `${departmentName} — ` : ""}
            Register, archive and track correspondence.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="ghost" size="sm" onClick={() => load()} disabled={loading}>
            <RefreshCw size={16} className={loading ? "animate-spin" : ""} />
          </Button>
          {canManage && (
            <>
              <Button size="sm" variant="outline" onClick={() => openCreate("incoming")}>
                <ArrowDownLeft size={16} className="mr-1" />
                New Incoming
              </Button>
              <Button size="sm" onClick={() => openCreate("outgoing")}>
                <ArrowUpRight size={16} className="mr-1" />
                New Outgoing
              </Button>
            </>
          )}
        </div>
      </div>

      <div className="flex-1 space-y-4 overflow-y-auto p-4">
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
          <Card>
            <CardHeader className="pb-2">
              <CardDescription className="flex items-center gap-2 text-xs">
                <ArrowDownLeft size={14} /> Total Incoming
              </CardDescription>
            </CardHeader>
            <CardContent>
              <p className="text-2xl font-semibold">{dashboard?.totalIncoming ?? 0}</p>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="pb-2">
              <CardDescription className="flex items-center gap-2 text-xs">
                <ArrowUpRight size={14} /> Total Outgoing
              </CardDescription>
            </CardHeader>
            <CardContent>
              <p className="text-2xl font-semibold">{dashboard?.totalOutgoing ?? 0}</p>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="pb-2">
              <CardDescription className="text-xs">Incoming in selected period</CardDescription>
            </CardHeader>
            <CardContent>
              <p className="text-2xl font-semibold">{dashboard?.periodIncoming ?? 0}</p>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="pb-2">
              <CardDescription className="text-xs">Outgoing in selected period</CardDescription>
            </CardHeader>
            <CardContent>
              <p className="text-2xl font-semibold">{dashboard?.periodOutgoing ?? 0}</p>
            </CardContent>
          </Card>
        </div>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base">Transactions by status</CardTitle>
          </CardHeader>
          <CardContent className="flex flex-wrap gap-2">
            {STATUS_ORDER.map((s) => (
              <Badge key={s} variant={statusVariant(s)} className="text-xs">
                {STATUS_LABELS[s]}: {statusCounts[s] ?? 0}
              </Badge>
            ))}
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base">Search &amp; filters</CardTitle>
          </CardHeader>
          <CardContent className="grid gap-3 md:grid-cols-5">
            <div className="md:col-span-2">
              <Label className="text-xs">Search</Label>
              <div className="relative">
                <Search size={14} className="absolute left-2 top-1/2 -translate-y-1/2 text-muted-foreground" />
                <Input
                  className="pl-7"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Number, title, notes, sender, recipient, or document content"
                />
              </div>
            </div>
            <div>
              <Label className="text-xs">Type</Label>
              <Select value={typeFilter} onValueChange={setTypeFilter}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All types</SelectItem>
                  <SelectItem value="incoming">Incoming</SelectItem>
                  <SelectItem value="outgoing">Outgoing</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">Status</Label>
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All statuses</SelectItem>
                  {STATUS_ORDER.map((s) => (
                    <SelectItem key={s} value={s}>
                      {STATUS_LABELS[s]}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <Label className="text-xs">From</Label>
                <Input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} />
              </div>
              <div>
                <Label className="text-xs">To</Label>
                <Input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <CardTitle className="text-base">Transactions</CardTitle>
              <span className="text-xs text-muted-foreground">{total} total</span>
            </div>
          </CardHeader>
          <CardContent className="space-y-2">
            {loading ? (
              <div className="flex items-center gap-2 py-8 text-sm text-muted-foreground">
                <Loader2 className="h-4 w-4 animate-spin" />
                Loading transactions...
              </div>
            ) : items.length === 0 ? (
              <p className="py-8 text-center text-sm text-muted-foreground">
                No transactions found. Register one using the buttons above.
              </p>
            ) : (
              items.map((item) => (
                <div
                  key={item._id}
                  className="flex flex-wrap items-center gap-3 rounded-md border p-3 hover:bg-muted/40"
                >
                  <div
                    className={cn(
                      "flex h-8 w-8 shrink-0 items-center justify-center rounded-full",
                      item.type === "incoming" ? "bg-blue-100 text-blue-700" : "bg-emerald-100 text-emerald-700"
                    )}
                  >
                    {item.type === "incoming" ? <ArrowDownLeft size={16} /> : <ArrowUpRight size={16} />}
                  </div>
                  <button
                    type="button"
                    className="min-w-0 flex-1 text-left"
                    onClick={() => openDetail(item)}
                  >
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="font-mono text-xs text-muted-foreground">{item.transactionNumber}</span>
                      <Badge variant={statusVariant(item.status)} className="text-[10px]">
                        {STATUS_LABELS[item.status] || item.status}
                      </Badge>
                      {item.attachmentCount > 0 && (
                        <span className="flex items-center gap-1 text-[10px] text-muted-foreground">
                          <Paperclip size={11} />
                          {item.attachmentCount}
                        </span>
                      )}
                    </div>
                    <p className="truncate font-medium">{item.title || "Untitled"}</p>
                    <p className="truncate text-xs text-muted-foreground">
                      {item.type === "incoming"
                        ? `From: ${item.senderName || "—"}${item.senderOrganization ? ` (${item.senderOrganization})` : ""}`
                        : `To: ${item.recipientName || "—"}${item.recipientOrganization ? ` (${item.recipientOrganization})` : ""}`}
                      {" · "}
                      {formatDate(item.transactionDate)}
                    </p>
                  </button>
                  <div className="flex items-center gap-1">
                    <Button variant="ghost" size="sm" onClick={() => openDetail(item)}>
                      View
                    </Button>
                    {canDelete && (
                      <Button
                        variant="ghost"
                        size="sm"
                        className="text-destructive"
                        onClick={() => setDeleteTarget(item)}
                      >
                        <Trash2 size={15} />
                      </Button>
                    )}
                  </div>
                </div>
              ))
            )}
          </CardContent>
        </Card>
      </div>

      {/* New transaction */}
      <Dialog
        open={createOpen}
        onOpenChange={(open) => {
          setCreateOpen(open);
          if (!open) resetForm();
        }}
      >
        <DialogContent className="max-h-[90vh] max-w-2xl overflow-y-auto">
          <DialogHeader>
            <DialogTitle>New Transaction</DialogTitle>
            <DialogDescription>
              The transaction number and QR code are generated automatically after saving.
            </DialogDescription>
          </DialogHeader>

          <div className="grid gap-4 py-2">
            <div className="grid gap-3 sm:grid-cols-2">
              <div>
                <Label className="text-xs">Transaction type</Label>
                <Select value={form.type} onValueChange={(v) => setForm((p) => ({ ...p, type: v }))}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="incoming">Incoming</SelectItem>
                    <SelectItem value="outgoing">Outgoing</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs">Transaction date</Label>
                <Input
                  type="date"
                  value={form.transactionDate}
                  onChange={(e) => setForm((p) => ({ ...p, transactionDate: e.target.value }))}
                />
              </div>
            </div>

            <div>
              <Label className="text-xs">Document title or subject *</Label>
              <Input
                value={form.title}
                onChange={(e) => setForm((p) => ({ ...p, title: e.target.value }))}
                placeholder="e.g. Supplier price revision letter"
              />
            </div>

            <div className="grid gap-3 sm:grid-cols-2">
              <div>
                <Label className="text-xs">Sender name</Label>
                <Input
                  value={form.senderName}
                  onChange={(e) => setForm((p) => ({ ...p, senderName: e.target.value }))}
                />
              </div>
              <div>
                <Label className="text-xs">Sender organization</Label>
                <Input
                  value={form.senderOrganization}
                  onChange={(e) => setForm((p) => ({ ...p, senderOrganization: e.target.value }))}
                />
              </div>
              <div>
                <Label className="text-xs">Recipient name</Label>
                <Input
                  value={form.recipientName}
                  onChange={(e) => setForm((p) => ({ ...p, recipientName: e.target.value }))}
                />
              </div>
              <div>
                <Label className="text-xs">Recipient organization</Label>
                <Input
                  value={form.recipientOrganization}
                  onChange={(e) => setForm((p) => ({ ...p, recipientOrganization: e.target.value }))}
                />
              </div>
            </div>

            <div className="grid gap-3 sm:grid-cols-2">
              <div>
                <Label className="text-xs">Status</Label>
                <Select value={form.status} onValueChange={(v) => setForm((p) => ({ ...p, status: v }))}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {STATUS_ORDER.map((s) => (
                      <SelectItem key={s} value={s}>
                        {STATUS_LABELS[s]}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs">Responsible department</Label>
                <Input value={departmentName || "Current department"} disabled />
              </div>
            </div>

            <div>
              <Label className="text-xs">Notes</Label>
              <Textarea
                rows={3}
                value={form.notes}
                onChange={(e) => setForm((p) => ({ ...p, notes: e.target.value }))}
              />
            </div>

            <div className="grid gap-3 sm:grid-cols-2">
              <div>
                <Label className="text-xs">Document file</Label>
                <Input
                  ref={documentInputRef}
                  type="file"
                  onChange={(e) => setDocumentFile(e.target.files?.[0] || null)}
                />
                {documentFile && (
                  <p className="mt-1 truncate text-xs text-muted-foreground">{documentFile.name}</p>
                )}
              </div>
              <div>
                <Label className="text-xs">Attachments</Label>
                <Input
                  ref={attachmentsInputRef}
                  type="file"
                  multiple
                  onChange={(e) => setAttachmentFiles(Array.from(e.target.files || []))}
                />
                {attachmentFiles.length > 0 && (
                  <p className="mt-1 text-xs text-muted-foreground">{attachmentFiles.length} file(s) selected</p>
                )}
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setCreateOpen(false)} disabled={saving}>
              Cancel
            </Button>
            <Button onClick={handleCreate} disabled={saving}>
              {saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Save transaction
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Detail */}
      <Dialog open={!!detail} onOpenChange={(open) => !open && setDetail(null)}>
        <DialogContent className="max-h-[90vh] max-w-3xl overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex flex-wrap items-center gap-2">
              <span className="font-mono text-sm">{detail?.transactionNumber}</span>
              {detail?.type && <Badge variant="outline">{TYPE_LABELS[detail.type] || detail.type}</Badge>}
              {detail?.status && (
                <Badge variant={statusVariant(detail.status)}>
                  {STATUS_LABELS[detail.status] || detail.status}
                </Badge>
              )}
            </DialogTitle>
            <DialogDescription>{detail?.title || "Untitled"}</DialogDescription>
          </DialogHeader>

          {detailLoading ? (
            <div className="flex items-center gap-2 py-8 text-sm text-muted-foreground">
              <Loader2 className="h-4 w-4 animate-spin" />
              Loading transaction...
            </div>
          ) : (
            <div className="space-y-4 py-2">
              {canManage ? (
                <div className="space-y-3 rounded border p-3">
                  <p className="flex items-center gap-2 text-sm font-medium">
                    <Pencil size={15} />
                    Edit details
                  </p>
                  <div className="grid gap-3 sm:grid-cols-2">
                    <div className="sm:col-span-2">
                      <Label className="text-xs">Title / subject</Label>
                      <Input
                        value={editForm.title}
                        onChange={(e) => setEditForm((p) => ({ ...p, title: e.target.value }))}
                      />
                    </div>
                    <div>
                      <Label className="text-xs">Sender</Label>
                      <Input
                        value={editForm.senderName}
                        onChange={(e) => setEditForm((p) => ({ ...p, senderName: e.target.value }))}
                      />
                    </div>
                    <div>
                      <Label className="text-xs">Sender organization</Label>
                      <Input
                        value={editForm.senderOrganization}
                        onChange={(e) =>
                          setEditForm((p) => ({ ...p, senderOrganization: e.target.value }))
                        }
                      />
                    </div>
                    <div>
                      <Label className="text-xs">Recipient</Label>
                      <Input
                        value={editForm.recipientName}
                        onChange={(e) => setEditForm((p) => ({ ...p, recipientName: e.target.value }))}
                      />
                    </div>
                    <div>
                      <Label className="text-xs">Recipient organization</Label>
                      <Input
                        value={editForm.recipientOrganization}
                        onChange={(e) =>
                          setEditForm((p) => ({ ...p, recipientOrganization: e.target.value }))
                        }
                      />
                    </div>
                    <div>
                      <Label className="text-xs">Transaction date</Label>
                      <Input
                        type="date"
                        value={editForm.transactionDate}
                        onChange={(e) =>
                          setEditForm((p) => ({ ...p, transactionDate: e.target.value }))
                        }
                      />
                    </div>
                    <div className="sm:col-span-2">
                      <Label className="text-xs">Notes</Label>
                      <Textarea
                        rows={3}
                        value={editForm.notes}
                        onChange={(e) => setEditForm((p) => ({ ...p, notes: e.target.value }))}
                      />
                    </div>
                  </div>
                  <Button size="sm" onClick={handleEditSave} disabled={editSaving}>
                    {editSaving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                    Save details
                  </Button>
                </div>
              ) : (
                <div className="grid gap-3 text-sm sm:grid-cols-2">
                  <div>
                    <p className="text-xs text-muted-foreground">Sender</p>
                    <p>{detail?.senderName || "—"}</p>
                    <p className="text-xs text-muted-foreground">{detail?.senderOrganization || ""}</p>
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground">Recipient</p>
                    <p>{detail?.recipientName || "—"}</p>
                    <p className="text-xs text-muted-foreground">{detail?.recipientOrganization || ""}</p>
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground">Transaction date</p>
                    <p>{formatDate(detail?.transactionDate)}</p>
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground">Created by</p>
                    <p>{detail?.createdByName || "—"}</p>
                    <p className="text-xs text-muted-foreground">{formatDateTime(detail?.createdAt)}</p>
                  </div>
                  {detail?.notes && (
                    <div className="sm:col-span-2">
                      <p className="text-xs text-muted-foreground">Notes</p>
                      <p className="whitespace-pre-wrap text-sm">{detail.notes}</p>
                    </div>
                  )}
                </div>
              )}

              {canManage && (
                <p className="text-xs text-muted-foreground">
                  Created by {detail?.createdByName || "—"}
                  {detail?.createdAt ? ` · ${formatDateTime(detail.createdAt)}` : ""}
                </p>
              )}

              <div className="rounded border p-3">
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <p className="flex items-center gap-2 text-sm font-medium">
                    <ScanText size={15} />
                    Search indexing
                  </p>
                  {canManage && (detail?.dmsFileId || (detail?.attachments || []).length > 0) && (
                    <Button size="sm" variant="outline" onClick={handleRetryScan} disabled={scanRetrying}>
                      {scanRetrying && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                      {detail?.scanStatus === "pending" || detail?.scanStatus === "processing"
                        ? "Scanning…"
                        : "Re-scan document"}
                    </Button>
                  )}
                </div>
                <p className="mt-2 text-xs text-muted-foreground">
                  {detail?.scanStatus === "ready"
                    ? "Document content and notes are searchable from the list above."
                    : detail?.scanStatus === "pending" || detail?.scanStatus === "processing"
                      ? "Reading document content for search…"
                      : detail?.scanStatus === "failed"
                        ? detail?.scanError || "Could not extract text from the document."
                        : detail?.scanStatus === "skipped"
                          ? "No document to index, or no extractable text."
                          : "Upload a PDF/DOCX to index its content for search."}
                </p>
                {detail?.scanStatus === "ready" && detail?.extractedText && (
                  <p className="mt-1 text-[11px] text-muted-foreground">
                    Indexed {String(detail.extractedText).length.toLocaleString()} characters
                    {detail.scannedAt ? ` · ${formatDateTime(detail.scannedAt)}` : ""}
                  </p>
                )}
              </div>

              <div className="space-y-3 rounded border p-3">
                <p className="flex items-center gap-2 text-sm font-medium">
                  <Paperclip size={15} />
                  Files
                </p>
                {detail?.dmsFileId ? (
                  <a
                    className="flex items-center gap-2 rounded border p-2 text-sm hover:bg-muted/40"
                    href={dmsApi.correspondence.getFileViewUrl(String(detail.dmsFileId))}
                    target="_blank"
                    rel="noreferrer"
                  >
                    <Paperclip size={14} />
                    {detail.originalFileName && detail.originalFileName !== "[object Object]"
                      ? detail.originalFileName
                      : `Main document (${detail.transactionNumber || "PDF"})`}
                  </a>
                ) : (
                  <p className="text-sm text-muted-foreground">No main document uploaded yet.</p>
                )}
                {(detail?.attachments || []).map((att: any) => (
                  <a
                    key={String(att.dmsFileId)}
                    className="flex items-center gap-2 rounded border p-2 text-sm hover:bg-muted/40"
                    href={dmsApi.correspondence.getFileViewUrl(String(att.dmsFileId))}
                    target="_blank"
                    rel="noreferrer"
                  >
                    <Paperclip size={14} />
                    <span className="truncate">
                      {att.originalName && att.originalName !== "[object Object]"
                        ? att.originalName
                        : "Attachment"}
                    </span>
                  </a>
                ))}

                {canManage && (
                  <div className="grid gap-3 border-t pt-3 sm:grid-cols-2">
                    <div>
                      <Label className="text-xs">
                        {detail?.dmsFileId ? "Replace main document" : "Attach main document"}
                      </Label>
                      <Input
                        ref={detailDocumentInputRef}
                        type="file"
                        className="mt-1"
                        onChange={(e) => setDetailDocumentFile(e.target.files?.[0] || null)}
                      />
                      <Button
                        size="sm"
                        className="mt-2"
                        onClick={handleUploadMainDocument}
                        disabled={fileUploading || !detailDocumentFile}
                      >
                        {fileUploading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                        {detail?.dmsFileId ? "Replace document" : "Upload document"}
                      </Button>
                    </div>
                    <div>
                      <Label className="text-xs">Add attachments</Label>
                      <Input
                        ref={detailAttachmentsInputRef}
                        type="file"
                        multiple
                        className="mt-1"
                        onChange={(e) =>
                          setDetailAttachmentFiles(Array.from(e.target.files || []))
                        }
                      />
                      {detailAttachmentFiles.length > 0 && (
                        <p className="mt-1 text-xs text-muted-foreground">
                          {detailAttachmentFiles.length} file(s) selected
                        </p>
                      )}
                      <Button
                        size="sm"
                        className="mt-2"
                        variant="outline"
                        onClick={handleUploadAttachments}
                        disabled={fileUploading || detailAttachmentFiles.length === 0}
                      >
                        {fileUploading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                        Add attachments
                      </Button>
                    </div>
                  </div>
                )}
              </div>

              {canManage && (
                <div className="flex flex-wrap items-end gap-2 rounded border p-3">
                  <div className="min-w-40">
                    <Label className="text-xs">Status</Label>
                    <Select value={detailStatus} onValueChange={setDetailStatus}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {STATUS_ORDER.map((s) => (
                          <SelectItem key={s} value={s}>
                            {STATUS_LABELS[s]}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <Button
                    size="sm"
                    onClick={handleStatusSave}
                    disabled={statusSaving || detailStatus === detail?.status}
                  >
                    {statusSaving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                    Update status
                  </Button>
                </div>
              )}

              <div className="rounded border p-3">
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <p className="flex items-center gap-2 text-sm font-medium">
                    <QrCode size={15} />
                    QR code
                  </p>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => detail?._id && openQrDialog(String(detail._id))}
                  >
                    Generate QR code
                  </Button>
                </div>
                <p className="mt-2 text-xs text-muted-foreground">
                  Scanning requires a DMS login, then opens this transaction and its document
                  directly — no searching. Re-generate and re-stamp older PDFs after QR changes.
                </p>
              </div>

              <div className="rounded border p-3">
                <p className="mb-2 flex items-center gap-2 text-sm font-medium">
                  <History size={15} />
                  Activity history
                </p>
                {history.length === 0 ? (
                  <p className="text-sm text-muted-foreground">No activity recorded yet.</p>
                ) : (
                  <div className="max-h-48 space-y-2 overflow-y-auto">
                    {history.map((h) => (
                      <div key={h._id} className="rounded bg-muted/40 p-2 text-xs">
                        <div className="flex flex-wrap items-center justify-between gap-2">
                          <span className="font-medium">{ACTION_LABELS[h.action] || h.action}</span>
                          <span className="text-muted-foreground">{formatDateTime(h.createdAt)}</span>
                        </div>
                        <p className="text-muted-foreground">
                          {h.userName ? `${h.userName}` : "System"}
                          {h.details ? ` — ${h.details}` : ""}
                        </p>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setDetail(null)}>
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={qrDialogOpen} onOpenChange={setQrDialogOpen}>
        <DialogContent className="max-h-[90vh] max-w-lg overflow-y-auto">
          <DialogHeader>
            <DialogTitle>QR code — {detail?.transactionNumber}</DialogTitle>
            <DialogDescription>
              Scanning requires login, then opens the document directly. Download or print the QR
              label, or insert it into the transaction PDF.
            </DialogDescription>
          </DialogHeader>

          {qrLoading ? (
            <div className="flex items-center gap-2 py-8 text-sm text-muted-foreground">
              <Loader2 className="h-4 w-4 animate-spin" />
              Generating QR code...
            </div>
          ) : (
            <div className="space-y-4 py-2">
              {qr?.dataUrl ? (
                <div className="flex flex-col items-center gap-2">
                  {/* eslint-disable-next-line @next/next/no-img-element */}
                  <img src={qr.dataUrl} alt="Transaction QR code" className="h-44 w-44" />
                  <p className="break-all text-center text-xs text-muted-foreground">{qr.targetUrl}</p>
                  <div className="flex flex-wrap justify-center gap-2">
                    <Button size="sm" variant="outline" asChild>
                      <a href={qr.dataUrl} download={`${detail?.transactionNumber || "transaction"}-qr.png`}>
                        <Download size={14} className="mr-1" />
                        Download PNG
                      </a>
                    </Button>
                    <Button size="sm" variant="outline" onClick={printQr}>
                      <Printer size={14} className="mr-1" />
                      Print label
                    </Button>
                  </div>
                </div>
              ) : (
                <p className="text-sm text-muted-foreground">QR preview unavailable.</p>
              )}

              {pdfInfo?.isPdf && canManage ? (
                <div className="space-y-3 rounded border p-3">
                  <p className="text-sm font-medium">Insert into document PDF</p>
                  <p className="text-xs text-muted-foreground">
                    {pdfInfo.originalName || "Document"} — {pdfInfo.pageCount} page(s)
                  </p>
                  <p className="text-xs text-muted-foreground">
                    Open the placement editor to drag the QR code to exactly where you want it on the page.
                  </p>
                  <Button
                    className="w-full"
                    onClick={() => {
                      setQrDialogOpen(false);
                      window.setTimeout(() => setQrEditorOpen(true), 100);
                    }}
                  >
                    Open placement editor
                  </Button>
                  {detail?.dmsFileId && (
                    <Button variant="outline" size="sm" className="w-full" asChild>
                      <a
                        href={dmsApi.correspondence.getFileViewUrl(String(detail.dmsFileId))}
                        target="_blank"
                        rel="noreferrer"
                      >
                        Open document PDF
                      </a>
                    </Button>
                  )}
                </div>
              ) : pdfInfo?.hasDocument && !pdfInfo.isPdf ? (
                <p className="rounded border border-dashed p-3 text-xs text-muted-foreground">
                  The attached file is not a PDF. Upload a PDF document to insert the QR code into it.
                </p>
              ) : !pdfInfo?.hasDocument ? (
                <p className="rounded border border-dashed p-3 text-xs text-muted-foreground">
                  No document file attached. Upload a PDF when registering the transaction to use PDF insertion.
                </p>
              ) : null}
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setQrDialogOpen(false)}>
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={qrEditorOpen} onOpenChange={setQrEditorOpen}>
        <DialogContent className="max-h-[95vh] max-w-3xl overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Place QR on PDF — {detail?.transactionNumber}</DialogTitle>
            <DialogDescription>
              Drag the QR code to the exact spot on the page, adjust size if needed, then insert.
            </DialogDescription>
          </DialogHeader>

          {qrEditorOpen && qr?.dataUrl && pdfInfo?.isPdf && detail?.dmsFileId ? (
            <QrPdfPlacer
              key={`${detail._id}-${detail.dmsFileId}`}
              fileId={String(detail.dmsFileId)}
              fetchPdfBlob={fetchPdfForEditor}
              qrDataUrl={qr.dataUrl}
              transactionNumber={detail.transactionNumber || ""}
              pages={
                pdfInfo.pages?.length
                  ? pdfInfo.pages
                  : Array.from({ length: pdfInfo.pageCount || 1 }, (_, i) => ({
                      pageNumber: i + 1,
                      width: 595,
                      height: 842,
                    }))
              }
              onApply={handleApplyCustomQr}
              onCancel={() => setQrEditorOpen(false)}
              applying={applyingQrToPdf}
            />
          ) : (
            <p className="py-6 text-sm text-muted-foreground">
              Generate the QR code first, then open the placement editor.
            </p>
          )}
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!deleteTarget} onOpenChange={(open) => !open && setDeleteTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this transaction?</AlertDialogTitle>
            <AlertDialogDescription>
              {deleteTarget?.transactionNumber} will be removed and its files moved to trash. This cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleting}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => {
                e.preventDefault();
                handleDelete();
              }}
              disabled={deleting}
            >
              {deleting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
